@if (worker && !conferenceState?.uuid) {
  <div
    class="d-flex cbw-worker-card mb-4"
    role="region"
    [attr.aria-label]="'Worker Information'"
    tabindex="0"
  >
    <div
      class="avatar d-flex justify-content-center align-items-start"
      role="img"
      [attr.aria-label]="(worker | translation: 'resource_name': lang) + ' avatar'"
    >
      @if (worker.avatar !== null) {
        <img
          #image
          class="cbw-avatar"
          [attr.alt]="(worker | translation: 'resource_name': lang) + ' avatar'"
          src="{{ workerAvatarUrl }}{{ worker.uuid }}/thumb_{{ worker.avatar }}"
          (error)="image.src= baseUrl + '/img/sample-avatar.png'"
        />
      } @else {
        <img
          [attr.alt]="(worker | translation: 'resource_name': lang) + ' avatar'"
          class="cbw-avatar"
          src="{{ baseUrl }}/img/sample-avatar.png"
        />
      }
    </div>

    <div class="cbw-detail_description">
      <div
        class="cbw-title"
        aria-live="polite"
        aria-label="Worker name"
      >
        @if (worker.is_resource === 0) {
          {{ worker | translation: 'resource_name': lang }}
        } @else if (worker.is_resource === 1) {
          @if (worker.is_multi_language === 1) {
            {{ worker | translation: 'booking_label': lang }}
          } @else if (worker.is_multi_language === 0) {
            {{ worker.resource_name }}
          }
        }
      </div>

      @if (worker.is_resource === 0 && worker.job_title?.trim()) {
        <div
          class="cbw-function"
          [innerHTML]="(worker | translation: 'job_title': lang) | trustHtml"
          aria-live="polite"
          [attr.aria-label]="'Job title'"
        ></div>
      } @else if (worker.is_resource !== 0 && worker.description?.trim()) {
        <div
          class="cbw-function"
          [innerHTML]="(worker | translation: 'description': lang) | trustHtml"
          aria-live="polite"
          [attr.aria-label]="'Description'"
        ></div>
      }

      @let displayStore = getSelectedStore(worker);
      @if (partner?.widget_show_resource_store_name === 1 && displayStore) {
        <div
          class="cbw-worker-store-location"
          aria-live="polite"
          [attr.aria-label]="'Store location'"
        >
          <span class="cbw-worker-store-location-name">{{ displayStore | translation: 'name' : lang }}</span>
          @if (displayStore.is_online === 0) {
            <span class="cbw-worker-store-location-separator" aria-hidden="true">, </span>
            <div class="cbw-worker-store-location-address whitespace-pre-line">
              @let address = {
                street: displayStore.street,
                zip: displayStore.zip,
                city: displayStore.city,
                country: displayStore.country | translation: 'name' : lang,
                country_code: displayStore.country?.identifier
              };
              {{ address | addressFormat: 'single' }}
            </div>
          }
        </div>
      }

      @if (worker?.attributes?.length) {
        <div
          role="list"
          aria-label="Worker Attributes"
          class="worker-attributes-list"
        >
          @for (attribute of worker.attributes; track attribute.uuid) {
            @if (attribute.type === 'select') {
              <div
                class="attribute-container cbw-attribute-container"
                role="listitem"
                [attr.aria-label]="(attribute | translation: 'name': lang) + ' attribute'"
              >
                <div class="cbw-attribute">
                  {{ attribute | translation: 'name': lang }}:
                </div>
                <div
                  class="attribute-value-section"
                  role="list"
                  [attr.aria-label]="(attribute | translation: 'name': lang) + ' values'"
                >
                  @for (value of attribute.attributes_values; track value.uuid; let last = $last) {
                    <div
                      class="attribute-value-img"
                      role="listitem"
                      [attr.aria-label]="value | translation: 'value': lang"
                    >
                      @if (value.icon_url !== null) {
                        <img
                          #image
                          class="cbw-attribute-icon"
                          [attr.alt]="(value | translation: 'value': lang) + ' icon'"
                          src="{{ value.icon_url }}"
                          (error)="image.src= baseUrl + '/img/sample-avatar.png'"
                        />
                      }
                      <div class="cbw-attribute-value">
                        {{ value | translation: 'value': lang }}
                        @if (!last) { <span aria-hidden="true">|</span> }
                      </div>
                    </div>
                  }
                </div>
              </div>
            } @else {
              <div
                class="cbw-attribute cbw-attribute-container"
                role="listitem"
                [attr.aria-label]="(attribute | translation: 'name': lang) + ' attribute'"
              >
                {{ attribute | translation: 'name': lang }}:
                @if (attribute.attributes_values?.length) {
                  {{ +attribute.attributes_values[0].value === 1 ? ( 'yes' | translate ): ( 'no' | translate ) }}
                }
              </div>
            }
          }
        </div>
      }
    </div>
  </div>
}

<div class="btn-group d-flex calendar-header align-items-center" role="toolbar" aria-label="Calendar Navigation">
  @if (!disablePrev) {
    <div
      class="btn w-100"
      mwlCalendarPreviousView
      view="month"
      [(viewDate)]="viewDate"
      (viewDateChange)="viewDateChanged(viewDate, false)"
      role="button"
      [attr.aria-label]="'Previous month' | translate"
      tabindex="0"
    >
      <div class="prev-arrow-container">
        <span>
          <fa-icon class="icon cbw-prev-arrow-button d-flex" icon="chevron-left" aria-hidden="true" />
        </span>
      </div>
    </div>
  } @else {
    <div class="btn w-100 disabled" role="button" aria-disabled="true">
      <div class="prev-arrow-container">
        <span>
          <fa-icon class="icon cbw-prev-arrow-button d-flex" icon="chevron-left" aria-hidden="true" />
        </span>
      </div>
    </div>
  }

  <div class="btn w-100 disabled" role="button" aria-disabled="true">
    <div class="calendar-header-button-text text-overflow-center">
      <h3>{{ viewDate | date: 'MMMM' | translate }}</h3>
      <p>{{ viewDate | date: 'y' }}</p>
    </div>
  </div>

  @if (!disableNext) {
    <div
      class="btn w-100"
      mwlCalendarNextView
      view="month"
      [(viewDate)]="viewDate"
      (viewDateChange)="viewDateChanged(viewDate, true)"
      role="button"
      [attr.aria-label]="'Next month' | translate"
      tabindex="0"
    >
      <div class="next-arrow-container">
        <span>
          <fa-icon class="icon cbw-next-arrow-button d-flex" icon="chevron-right" aria-hidden="true" />
        </span>
      </div>
    </div>
  } @else {
    <div class="btn w-100 disabled" role="button" aria-disabled="true">
      <div class="next-arrow-container">
        <span>
          <fa-icon class="icon cbw-next-arrow-button d-flex" icon="chevron-right" aria-hidden="true" />
        </span>
      </div>
    </div>
  }
</div>

<ng-template #customCellTemplate let-day="day" let-locale="locale" let-tooltipTemplate="tooltipTemplate">
  <div
    class="cal-cell-wrapper cbw-calendar-cell-wrapper"
    [ngbTooltip]="highlightedDatesDataSet?.[day.date | date: 'yyyy-MM-dd']?.data ? highlightDescription : undefined"
    [ngClass]="highlightedDatesDataSet?.[day.date | date: 'yyyy-MM-dd']?.css"
    role="gridcell"
    [attr.aria-selected]="day.cssClass === 'cal-selected'"
    [attr.aria-disabled]="!day.isInCurrentMonth"
    [attr.aria-label]="day.date | date: 'MMMM d, y'"
  >
    <ng-template #highlightDescription>
      <span
        [innerHTML]="highlightedDatesDataSet?.[day.date | date: 'yyyy-MM-dd']?.data | translation: 'description' : lang  | trustHtml"
      ></span>
    </ng-template>

    <div class="cal-cell-top">
      <div
        class="cal-day-number"
        [ngClass]="{
          'cal-debug': isTroubleshootEnabled,
          'debug-day-availability-state-0': isTroubleshootEnabled && day?.meta?.dayStatus === 0,
          'debug-day-availability-state-1': isTroubleshootEnabled && day?.meta?.dayStatus === 1,
          'debug-day-availability-state-2': isTroubleshootEnabled && day?.meta?.dayStatus === 2,
        }"
        [ngStyle]="getColorOfDay(day) | async"
        openDelay="500"
      >
        <div [ngClass]="{ 'calendar-total-badge' : day.badgeTotal > 0 }">
          <span>{{ day.date | calendarDate:'monthViewDayNumber' : locale }}</span>
          @if (day.badgeTotal > 0 && day.cssClass === 'cal-selected') {
            <span
              class="badge badge-primary custom-slots-badge"
              [ngClass]="{
                'single-digit-badge-adjustment': (day.date | calendarDate : 'monthViewDayNumber' : locale) < 10,
                'double-digit-badge-adjustment': (day.date | calendarDate : 'monthViewDayNumber' : locale) >= 10
              }"
              [attr.aria-label]="day.badgeTotal + ' slots available'"
            >
              {{ day.badgeTotal }}
            </span>
          }
        </div>
      </div>
    </div>
  </div>
</ng-template>

@if (!calendarLoaded) {
  <div class="loader-box" role="status" aria-label="Loading calendar">
    <app-loader aria-label="Loading calendar" />
  </div>
}

<div [ngClass]="{ 'd-none': !calendarLoaded }" role="grid" aria-label="Calendar">
  <mwl-calendar-month-view
    weekStartsOn=1
    activeDayIsOpen=true
    [viewDate]="viewDate"
    [weekendDays]="[]"
    [headerTemplate]="shortDays"
    [cellTemplate]="customCellTemplate"
    [refresh]="refreshCalendar"
    (dayClicked)="dayClicked($event)"
    (beforeViewRender)="markExceptionDays($event);"
  />
</div>


@if (partner?.is_timezone_enabled) {
  <div class="mt-20 mb-2 cbw-timezone-wrapper">
    @if (partner?.widget_display_timezone_source !== widgetDisplayTimezoneSource.PARTNER || !partner?.timezone) {
      <label class="fw-semibold cbw-timezone-label">
        {{ "timezone" | translate }}:
      </label>
    }
    <app-timezone-select
      [(userState)]="userState"
      [timezones]="timezones"
      [partner]="partner"
      (onTimezoneChange)="onTimezoneChangeEvent.emit($event)"
    />
  </div>
}

<!--Calendar subheader week days in shot form (Mo, Tu)-->
<ng-template #shortDays>
  <div class="cal-cell-row cal-header" role="row">
    @for (day of days | slice:0:7; track day) {
      <div
        class="cal-cell"
        [class.cal-past]="day.isPast"
        [class.cal-today]="day.isToday"
        [class.cal-future]="day.isFuture"
        [class.cal-weekend]="day.isWeekend"
        [ngClass]="day.cssClass"
        role="columnheader"
        [attr.aria-label]="'day' + day.date.getDay() | translate"
      >
        {{ 'day' + day.date.getDay() | translate }}
      </div>
    }
  </div>
</ng-template>
